import { AccountInfo, EventMessage, EventType } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BarChartIcon from '@mui/icons-material/BarChart';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GroupIcon from '@mui/icons-material/Group';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import MenuIcon from '@mui/icons-material/Menu';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { Box, Button, ClickAwayListener, CSSObject, Drawer, Fade, Grid, Paper, Popper } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import svLocale from 'date-fns/locale/sv';
import { useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import AlertDialog from './common/AlertDialog';
import GuardedListItemLink from './common/auth/GuardedListItemLink';
import GuardedRoute from './common/auth/GuardedRoute';
import ConfirmDialog from './common/ConfirmDialog';
import { useAppAjaxInit } from './common/hooks/useAppAjaxInit';
import { ListItemLink } from './common/ListItemLink';
import { ResourceType } from './common/PricePrgClient';
import { UserAvatar } from './common/UserAvatar';
import ActivityLog from './features/activitylog/ActivityLog';
import Agreement from './features/agreement/Agreement';
import { BasePrice } from './features/baseprice/BasePrice';
import Calculation from './features/calculation/Calculation';
import Customer from './features/customer/Customer';
import CustomerGroups from './features/customergroups/CustomerGroups';
import { Dashboard } from './features/dashboard/Dashboard';
import ExternalPrice from './features/externalprice/ExternalPrice';
import ExternalPricelist from './features/externalpricelist/ExternalPricelist';
import InactivatePrices from './features/inactivateprices/InactivatePrices';
import Price from './features/price/Price';
import Pricelist from './features/pricelist/Pricelist';
import Product from './features/product/Product';
import { getAppSettings } from './features/settings/redux/settingsSelectors';
import { Settings } from './features/settings/Settings';
import Snackbars from './features/ui/Snackbars';
import { loginSuccessAction, logoutSuccessful } from './features/user/redux/userSlices';
import User from './features/user/User';
import UserGroup from './features/usergroup/UserGroup';
import { RootState } from './store';
import { appEnvironment, custName } from './utils';

const mapStateToProps = (state: RootState) => ({
    appSettings: getAppSettings(state)
});

type OwnProps = {
    accountInfo: AccountInfo | null;
    logout(): void;
};
type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const drawerWidth = 240;

let envStr = '';
if (appEnvironment != null && appEnvironment !== 'Production') {
    envStr = ` - ${appEnvironment}`;
}
if (custName != null && custName.length > 0) {
    envStr = ` - ${custName}${envStr}`;
}

const initialDrawerOpenStr = localStorage.getItem('drawerOpen');
const initialDrawerOpen = initialDrawerOpenStr == null || initialDrawerOpenStr === 'true';

const openedMixin = (theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen
    }),
    overflowX: 'hidden'
});

const closedMixin = (theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`
    }
});
const App = ({ accountInfo, logout, appSettings }: Props) => {
    const [open, setOpen] = useState(initialDrawerOpen);
    const anchorRef = useRef<HTMLButtonElement>(null);
    const { instance } = useMsal();
    const [userPopperOpen, setUserPopperOpen] = useState(false);

    const handleDrawer = (isOpen: boolean) => () => {
        setOpen(isOpen);
        localStorage.setItem('drawerOpen', String(isOpen));
    };
    const dispatch = useDispatch();
    const handlePopperClose = event => {
        const target = event.target as HTMLElement;
        if (!anchorRef.current?.contains(target)) {
            setUserPopperOpen(false);
        }
    };
    instance.addEventCallback((message: EventMessage) => {
        if (message.eventType === EventType.LOGIN_SUCCESS) {
            dispatch(loginSuccessAction(message.payload as AccountInfo));
        } else if (message.eventType === EventType.LOGOUT_SUCCESS) {
            dispatch(logoutSuccessful());
        }
    });
    useAppAjaxInit();

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={svLocale}>
            <BrowserRouter>
                <Box sx={{ display: 'flex', paddingRight: '16px', paddingLeft: '16px' }}>
                    <CssBaseline />
                    <AppBar
                        position="absolute"
                        sx={theme => ({
                            zIndex: theme.zIndex.drawer + 1,
                            transition: theme.transitions.create(['width', 'margin'], {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.leavingScreen
                            }),
                            ...(open && {
                                marginLeft: drawerWidth,
                                width: `calc(100% - ${drawerWidth}px)`,
                                transition: theme.transitions.create(['width', 'margin'], {
                                    easing: theme.transitions.easing.sharp,
                                    duration: theme.transitions.duration.enteringScreen
                                })
                            })
                        })}
                    >
                        <Toolbar sx={{ paddingRight: 24, paddingLeft: 16 }}>
                            <Grid
                                container
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Grid item>
                                    <Grid container direction="row" alignItems="center">
                                        <IconButton
                                            edge="start"
                                            color="inherit"
                                            aria-label="open drawer"
                                            onClick={handleDrawer(true)}
                                            sx={() => {
                                                const x = { marginRight: 36 };
                                                if (open) {
                                                    return { ...x, display: 'none' };
                                                }
                                                return x;
                                            }}
                                        >
                                            <MenuIcon />
                                        </IconButton>
                                        <Typography
                                            component="h1"
                                            variant="h6"
                                            color="inherit"
                                            noWrap
                                            sx={{ flexGrow: 1 }}
                                        >
                                            {'Price program' + envStr}
                                        </Typography>
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item>
                                            <Typography variant="body2" color="inherit">
                                                {appSettings.applicationVersion}
                                            </Typography>
                                        </Grid>
                                        <Divider
                                            orientation="vertical"
                                            light
                                            flexItem
                                            sx={{ background: 'rgba(255, 255, 255, .7)', margin: '18px 4px' }}
                                        />
                                        <Grid item>
                                            {accountInfo != null && (
                                                <>
                                                    <Popper
                                                        open={userPopperOpen}
                                                        anchorEl={anchorRef.current}
                                                        placement="bottom-end"
                                                        transition
                                                    >
                                                        {({ TransitionProps }) => (
                                                            <ClickAwayListener
                                                                onClickAway={handlePopperClose}
                                                            >
                                                                <Fade {...TransitionProps} timeout={350}>
                                                                    <div>
                                                                        <Paper
                                                                            sx={theme => ({
                                                                                padding: theme.spacing(2)
                                                                            })}
                                                                        >
                                                                            <Button onClick={() => logout()}>
                                                                                Sign out
                                                                            </Button>
                                                                        </Paper>
                                                                    </div>
                                                                </Fade>
                                                            </ClickAwayListener>
                                                        )}
                                                    </Popper>
                                                    <IconButton
                                                        edge="start"
                                                        color="inherit"
                                                        sx={{ fontSize: 16 }}
                                                        onClick={() => setUserPopperOpen(x => !x)}
                                                        size="small"
                                                        ref={anchorRef}
                                                    >
                                                        <UserAvatar
                                                            user={{
                                                                fullName: accountInfo.name ?? '',
                                                                profileIconUrl: `api/users/${accountInfo.localAccountId}/photo`
                                                            }}
                                                            namePlacement="start"
                                                            iconOnly={false}
                                                        />
                                                    </IconButton>
                                                </>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Toolbar>
                    </AppBar>
                    <Drawer
                        variant="permanent"
                        sx={theme => ({
                            width: drawerWidth,
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                            boxSizing: 'border-box',
                            ...(open && {
                                ...openedMixin(theme),
                                '& .MuiDrawer-paper': openedMixin(theme)
                            }),
                            ...(!open && {
                                ...closedMixin(theme),
                                '& .MuiDrawer-paper': closedMixin(theme)
                            })
                        })}
                        open={open}
                    >
                        <Box
                            sx={theme => ({
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                padding: '0 8px',
                                ...theme.mixins.toolbar
                            })}
                        >
                            <IconButton onClick={handleDrawer(false)}>
                                <ChevronLeftIcon />
                            </IconButton>
                        </Box>
                        <Divider />
                        <List>
                            <ListItemLink
                                to="/dashboard"
                                primary="Dashboard"
                                icon={<DashboardIcon />}
                                tooltip={open ? undefined : 'Dashboard'}
                            />
                            <GuardedListItemLink
                                resource={ResourceType.CalculationPage}
                                link="/calculations"
                                name="Calculations"
                                icon={<BarChartIcon />}
                                tooltip={open ? undefined : 'Calculations'}
                            />
                            <GuardedListItemLink
                                resource={ResourceType.CustomerPage}
                                link="/customers"
                                name="Customers"
                                icon={<PersonIcon />}
                                tooltip={open ? undefined : 'Customers'}
                            />
                            <GuardedListItemLink
                                resource={ResourceType.ProductPage}
                                link="/products"
                                name="Products"
                                icon={<ShoppingCartIcon />}
                                tooltip={open ? undefined : 'Products'}
                            />
                            <Divider />
                            <GuardedListItemLink
                                resource={ResourceType.BasePricePage}
                                link="/baseprices"
                                name="Base prices"
                                icon={<AttachMoneyIcon />}
                                tooltip={open ? undefined : 'Base prices'}
                            />
                            <GuardedListItemLink
                                resource={ResourceType.PricePage}
                                link="/prices"
                                name="Prices"
                                icon={<AttachMoneyIcon />}
                                tooltip={open ? undefined : 'Prices'}
                            />
                            <GuardedListItemLink
                                resource={ResourceType.ExternalPricePage}
                                link="/externalprices"
                                name="External prices"
                                icon={<AttachMoneyIcon />}
                                tooltip={open ? undefined : 'External prices'}
                            />
                            <GuardedListItemLink
                                resource={ResourceType.InactivatePricesPage}
                                link="/inactivate-prices"
                                name="Inactivate prices"
                                icon={<MoneyOffIcon />}
                                tooltip={open ? undefined : 'Inactivate prices'}
                            />
                            <Divider />
                            <GuardedListItemLink
                                resource={ResourceType.PricelistPage}
                                link="/pricelists"
                                name="Pricelists"
                                icon={<AttachMoneyIcon />}
                                tooltip={open ? undefined : 'Pricelists'}
                            />
                            <GuardedListItemLink
                                resource={ResourceType.ExternalPricelistPage}
                                link="/externalpricelists"
                                name="External pricelists"
                                icon={<AttachMoneyIcon />}
                                tooltip={open ? undefined : 'External pricelists'}
                            />
                            <Divider />
                            <GuardedListItemLink
                                resource={ResourceType.AgreementPage}
                                link="/agreements"
                                name="Agreements"
                                icon={<LocalOfferIcon />}
                                tooltip={open ? undefined : 'Agreements'}
                            />
                            <GuardedListItemLink
                                resource={ResourceType.AgreementCustomerGroupPage}
                                link="/agreement-customer-groups"
                                name="Agreement groups"
                                icon={<GroupIcon />}
                                tooltip={open ? undefined : 'Agreement groups'}
                            />
                            <Divider />
                            <GuardedListItemLink
                                resource={ResourceType.UserPage}
                                link="/users"
                                name="Users"
                                icon={<PersonIcon />}
                                tooltip={open ? undefined : 'Users'}
                            />
                            <GuardedListItemLink
                                resource={ResourceType.UserGroupPage}
                                link="/user-groups"
                                name="User groups"
                                icon={<GroupIcon />}
                                tooltip={open ? undefined : 'User groups'}
                            />
                            <Divider />
                            <GuardedListItemLink
                                resource={ResourceType.ActivityLogPage}
                                link="/activity-logs"
                                name="Activity logs"
                                icon={<AssignmentIcon />}
                                tooltip={open ? undefined : 'Activity logs'}
                            />
                            <GuardedListItemLink
                                resource={ResourceType.SettingsPage}
                                link="/settings"
                                name="Settings"
                                icon={<SettingsIcon />}
                                tooltip={open ? undefined : 'Settings'}
                            />
                        </List>
                    </Drawer>
                    <main style={{ marginTop: '20px', flexGrow: 1, height: '97vh', overflow: 'none' }}>
                        <Box sx={theme => ({ ...theme.mixins.toolbar })} />
                        {/* <Box
                            sx={theme => ({
                                paddingTop: theme.spacing(2),
                                paddingBottom: theme.spacing(2),
                                height: '97vh'
                            })}
                        > */}
                        <Switch>
                            <Route path="/dashboard" component={Dashboard} />
                            <GuardedRoute
                                resource={ResourceType.CalculationPage}
                                path="/calculations"
                                component={Calculation}
                            />
                            <GuardedRoute
                                resource={ResourceType.CustomerPage}
                                path="/customers"
                                component={Customer}
                            />
                            <GuardedRoute
                                resource={ResourceType.BasePricePage}
                                path="/baseprices"
                                component={BasePrice}
                            />
                            <GuardedRoute
                                resource={ResourceType.PricePage}
                                path="/prices"
                                component={Price}
                            />
                            <GuardedRoute
                                resource={ResourceType.PricelistPage}
                                path="/pricelists"
                                component={Pricelist}
                            />
                            <GuardedRoute
                                resource={ResourceType.AgreementPage}
                                path="/agreements"
                                component={Agreement}
                            />
                            <GuardedRoute
                                resource={ResourceType.ExternalPricelistPage}
                                path="/externalpricelists"
                                component={ExternalPricelist}
                            />
                            <GuardedRoute
                                resource={ResourceType.ExternalPricePage}
                                path="/externalprices"
                                component={ExternalPrice}
                            />
                            <GuardedRoute
                                resource={ResourceType.InactivatePricesPage}
                                path="/inactivate-prices"
                                component={InactivatePrices}
                            />
                            <GuardedRoute
                                resource={ResourceType.ProductPage}
                                path="/products"
                                component={Product}
                            />
                            <GuardedRoute resource={ResourceType.UserPage} path="/users" component={User} />
                            <GuardedRoute
                                resource={ResourceType.UserGroupPage}
                                path="/user-groups"
                                component={UserGroup}
                            />
                            <GuardedRoute
                                resource={ResourceType.AgreementCustomerGroupPage}
                                path="/agreement-customer-groups"
                                component={CustomerGroups}
                            />
                            <GuardedRoute
                                resource={ResourceType.SettingsPage}
                                path="/settings"
                                component={Settings}
                            />
                            <GuardedRoute
                                resource={ResourceType.ActivityLogPage}
                                path="/activity-logs"
                                component={ActivityLog}
                            />
                        </Switch>
                        {/* </Box> */}
                    </main>
                </Box>
            </BrowserRouter>
            <ConfirmDialog />
            <AlertDialog />
            <Snackbars />
        </LocalizationProvider>
    );
};

export default connect(mapStateToProps)(App);
